import { isGeneId, isPhenotypeId } from 'src/util/util';
import { normalizeFetuses } from './pregnancyNormalize';

// Fetus diagnoses as FIRST-CLASS family diseases (Tim's decision 20).
//
// A fetus diagnosis is stored inside its pregnancy record (fetus-schema embeds the same
// `diagnosesSchema` a person uses), so it is invisible to the two family-level registries the render
// consults — `labelsSet.diseasesMap[id]` gates visibility (Canvas.jsx) and the canonical
// `diseaseColorMap[id]` now resolves the fill, with the record's own colour as the fallback
// (TGCLOUD-1504 remediation B4, carrier-status/diagnoseSegments.js). Both are fed from the server's
// family diagnoses, which are gathered from MEMBERS. Without the helpers here a recorded fetus
// diagnosis would persist, reach the render node, and then silently fail to paint — and would be
// missing from the Legend that explains the colours.
//
// These are pure so the registry wiring is pinned by tests rather than only exercised through d3.

// The palette is no longer mirrored here. TGCLOUD-1793 Phase 1 collapsed this
// copy, the server's and the picker's into one file both sides read
// (backend/util/disease-palette.json). Re-exported under the original name so
// existing imports keep working.
export { FIXED_DIAGNOSES_COLORS } from 'src/util/diseasePalette';

// Every diagnosis recorded against any fetus of any pregnancy on these members, de-duplicated by
// disease id. `normalizeFetuses` is used rather than reading `fetuses` directly so a pregnancy written
// before the fetus sub-document existed still contributes its implicit fetus's diagnoses.
export function collectFetusDiagnoses(memberRecords) {
  const byId = new Map();
  (memberRecords || []).forEach((member) => {
    (member?.pregnancies || []).forEach((record) => {
      normalizeFetuses(record).forEach((fetus) => {
        (fetus?.diagnoses || []).forEach((dx) => {
          if (!dx || dx.id == null || byId.has(dx.id)) return;
          byId.set(dx.id, dx);
        });
      });
    });
  });
  return [...byId.values()];
}

// The disease ids a fetus diagnosis contributes — what the visibility map needs.
export function fetusDiagnosisIds(memberRecords) {
  return collectFetusDiagnoses(memberRecords).map((dx) => dx.id);
}

// Build the stored diagnosis entry for a fetus from a disease-search result. Shape matches
// `diagnosesSchema` so it is the same record a person carries — which is what makes REQ-I5's
// carry-over on birth a plain array copy, and what lets the existing shading pass consume it.
//
// `displayOnPedigree` is true because the render filter requires it; `age` is deliberately omitted (a
// fetus has no age of diagnosis — gestation is the analogue and lives on the pregnancy); and
// `affectedStatus` is left unset, which the shading pass treats as affected (it only excludes an
// explicit 'unaffected'/'unknown').
//
// NO `color`. TGCLOUD-1793 Phase 1 removed the browser-side allocator that used to mint one here:
// it had a different exhaustion rule from the server's, a final fallback that knowingly returned a
// duplicate, and it could only see the diagnoses the open chart happened to hold, so it could hand
// a colour a relative already used. The server now allocates on save
// (backend/util/fetusDiagnosisColours.js) and echoes the coloured fetuses back in the response,
// which `submitPregnancy` merges into memberRecords.
export function buildFetusDiagnosis(disease, { recordedBy } = {}) {
  return {
    id: disease.id,
    name: disease.name,
    displayOnPedigree: true,
    isPatientReported: false,
    clinicallyConfirmed: false,
    ...(recordedBy ? { recordedBy } : {})
  };
}

// Merge fetus diseases into a family disease list for the colour map, without disturbing the
// canonical entries: a disease already known to the family keeps its server-assigned colour.
export function withFetusDiseases(familyDiagnoses, memberRecords) {
  const known = new Set(
    (familyDiagnoses || [])
      .filter((dx) => dx && dx.id != null)
      .map((dx) => String(dx.id))
  );
  const extra = collectFetusDiagnoses(memberRecords).filter(
    (dx) => !known.has(String(dx.id))
  );
  return extra.length
    ? [...(familyDiagnoses || []), ...extra]
    : familyDiagnoses;
}

// === DYSMORPHIC FEATURES (REQ-E1) ==========================================================
// HPO terms on a fetus. A person's phenotypes are saved via POST /phenotypes, which requires a
// familyMemberId a fetus does not have — so, as with diagnoses, the fetus's live on the pregnancy
// record and are merged in at the render boundary.
//
// These two helpers USED to feed a `phenotypesMap` merge in PedigreeChart, because the phenotype
// LABEL pass gated on `labelsSet.phenotypesMap[id]` exactly as shading still gates on
// `diseasesMap[id]`, and that map is fed from member data which cannot see inside a pregnancy
// record. TGCLOUD-1820 decoupled the legend maps from the labels, so that merge is gone and the
// gate with it: a fetus's phenotypes label because deriveReproNodes copies them onto the fetus
// node's `phenotypes` array, which `phenotypeUnion` reads directly.
//
// They have NO production caller today — only fetusDiagnoses.test.js. They are kept as the one
// declared reader of fetus phenotype data (`withFetusDiseases`'s counterpart) for the day the
// Phenotypes legend lists a fetus's terms. This comment used to reserve them for the disease
// colour-key work; it no longer does. TGCLOUD-1793 is a DISEASE key and puts phenotypes explicitly
// out of scope, so nothing in that ticket will call these — do not keep them on its account.

// The disease search returns diseases, genes and phenotypes mixed together, and the app routes them by
// id prefix. Sprint 96 (TGC-1586) added exactly these predicates to src/util/util.js, so they are
// re-exported from there rather than defined twice — two copies of a routing rule that decides whether
// a term becomes a DIAGNOSIS or a FEATURE is precisely the kind of thing that drifts. The guard against
// a non-string id stays here, since the shared versions use optional chaining and would return
// undefined rather than false.
export const isPhenotypeTermId = (id) =>
  typeof id === 'string' && isPhenotypeId(id);

export const isGeneTermId = (id) => typeof id === 'string' && isGeneId(id);

// Every phenotype recorded against any fetus, de-duplicated by term id.
export function collectFetusPhenotypes(memberRecords) {
  const byId = new Map();
  (memberRecords || []).forEach((member) => {
    (member?.pregnancies || []).forEach((record) => {
      normalizeFetuses(record).forEach((fetus) => {
        (fetus?.phenotypes || []).forEach((p) => {
          if (!p || p.id == null || byId.has(p.id)) return;
          byId.set(p.id, p);
        });
      });
    });
  });
  return [...byId.values()];
}

export function fetusPhenotypeIds(memberRecords) {
  return collectFetusPhenotypes(memberRecords).map((p) => p.id);
}

// The stored phenotype entry for a fetus, shaped like a person's (the `entitySchema` field set), so the
// existing label pass consumes it unchanged. `observed` defaults TRUE: recording a feature means it was
// seen — an explicitly-absent feature is a separate clinical statement the clinician sets deliberately.
export function buildFetusPhenotype(term, { recordedBy } = {}) {
  return {
    id: term.id,
    name: term.name,
    observed: true,
    displayOnPedigree: true,
    clinicallyConfirmed: false,
    patientReported: false,
    ...(recordedBy ? { recordedBy } : {})
  };
}
